/**
 * Global keyboard shortcuts – single source of truth for keys and UI labels.
 * Use with react-hotkeys-hook (useHotkeys) and show shortcuts in the UI via getShortcutDisplay().
 *
 * Add new shortcuts here and register them with useHotkeys() where the action runs.
 */

export const SHORTCUT_KEYS = {
  /** Open menu / command search (works everywhere) */
  MENU_SEARCH: 'mod+k',
  /** Go to Dashboard */
  GO_DASHBOARD: 'mod+1',
  /** Open Preferences / customization panel */
  PREFERENCES: 'mod+,',
  /** Save current form (form pages listen for app-save-form event) */
  SAVE_FORM: 'mod+s',
} as const;

export type ShortcutId = keyof typeof SHORTCUT_KEYS;

export interface ShortcutDef {
  id: ShortcutId;
  /** Keys for useHotkeys(), e.g. 'mod+k' */
  keys: string;
  /** Human-readable label for the action */
  label: string;
}

/** All global shortcuts for listing in Keyboard Shortcuts modal / help */
export const GLOBAL_SHORTCUTS: ShortcutDef[] = [
  { id: 'MENU_SEARCH', keys: SHORTCUT_KEYS.MENU_SEARCH, label: 'Search menu / go to page' },
  { id: 'GO_DASHBOARD', keys: SHORTCUT_KEYS.GO_DASHBOARD, label: 'Go to Dashboard' },
  { id: 'PREFERENCES', keys: SHORTCUT_KEYS.PREFERENCES, label: 'Open Preferences' },
  { id: 'SAVE_FORM', keys: SHORTCUT_KEYS.SAVE_FORM, label: 'Save form' },
];

/** Use in UI: returns e.g. "⌘K" on Mac, "Ctrl+K" on Windows */
export function getShortcutDisplay(keys: string): string {
  if (typeof navigator === 'undefined') return 'Ctrl+K';
  const isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
  if (keys === 'mod+k') return isMac ? '⌘K' : 'Ctrl+K';
  if (keys === 'mod+1') return isMac ? '⌘1' : 'Ctrl+1';
  if (keys === 'mod+,') return isMac ? '⌘,' : 'Ctrl+,';
  if (keys === 'mod+s') return isMac ? '⌘S' : 'Ctrl+S';
  if (keys.startsWith('mod+')) return keys.replace('mod+', isMac ? '⌘' : 'Ctrl+');
  return keys;
}

/** Custom event name for global save: form pages listen and call their submit handler */
export const GLOBAL_SAVE_FORM_EVENT = 'app-save-form';
